import { useMemo, useState } from 'react'
import { LayoutTemplate } from 'lucide-react'

import { Badge } from 'components/ui/badge'
import { Tabs, TabsList, TabsTrigger } from 'components/ui/tabs'
import { cn } from 'lib/utils'

import type { DeckBind, PresentationScope, PresentationTemplate } from '../types/presentation.types'
import { SCOPE_OPTIONS } from './AssetPicker'

/**
 * The deck templates to start a deck from (MYD-604): the starters and the
 * user's own, under a tab per kind of asset a deck is started on.
 *
 * With `scope` fixed, as from an asset's page, only that kind's templates are
 * shown and there are no tabs: a location page cannot start a project deck.
 */
interface DeckTemplateGalleryProps {
	/** Null while loading. */
	templates: PresentationTemplate[] | null
	/** Fix the kind of asset, and hide the tabs. */
	scope?: PresentationScope
	selectedKey: string | null
	onSelect: (template: PresentationTemplate) => void
}

type Tab = 'all' | PresentationScope

/** What a section beyond the first adds, in words. */
const SECTION_WORDS: Record<DeckBind, string | null> = {
	primary: null,
	'primary.location': 'its location',
	'primary.locations[]': 'a story per location',
	'primary.projects[]': 'a story per project',
}

/** "Plus a story per project", or nothing for a deck about one asset only. */
export const sectionSummary = (template: PresentationTemplate): string | null => {
	const extras = [...new Set((template.sections ?? []).map(section => SECTION_WORDS[section.bind]).filter(Boolean))]
	return extras.length ? `Plus ${extras.join(' and ')}` : null
}

const scopeLabel = (scope: PresentationScope) => SCOPE_OPTIONS.find(option => option.scope === scope)?.label ?? scope

export function DeckTemplateGallery({ templates, scope, selectedKey, onSelect }: DeckTemplateGalleryProps) {
	const [tab, setTab] = useState<Tab>('all')

	// Only the tabs that have something in them, in the asset picker's order.
	const tabs = useMemo(
		() => SCOPE_OPTIONS.map(option => option.scope).filter(candidate => (templates ?? []).some(template => template.scope === candidate)),
		[templates],
	)

	const shown = (templates ?? []).filter(template => {
		if (scope) return template.scope === scope
		return tab === 'all' || template.scope === tab
	})

	if (templates === null) return <p className="py-6 text-center text-sm text-muted-foreground">Loading templates...</p>

	return (
		<div className="space-y-3">
			{!scope && tabs.length > 1 && (
				<Tabs value={tab} onValueChange={value => setTab(value as Tab)}>
					<TabsList aria-label="Kind of asset">
						<TabsTrigger value="all">All</TabsTrigger>
						{tabs.map(candidate => (
							<TabsTrigger key={candidate} value={candidate}>
								{scopeLabel(candidate)}
							</TabsTrigger>
						))}
					</TabsList>
				</Tabs>
			)}

			{shown.length === 0 ? (
				<p className="py-6 text-center text-sm text-muted-foreground">
					{scope ? `No templates start from a ${scopeLabel(scope).toLowerCase()} yet.` : 'No templates yet.'}
				</p>
			) : (
				<div role="radiogroup" aria-label="Templates" className="grid max-h-[320px] grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2">
					{shown.map(template => {
						const selected = template.key === selectedKey
						const extra = sectionSummary(template)
						return (
							<button
								key={template.key}
								type="button"
								role="radio"
								aria-checked={selected}
								onClick={() => onSelect(template)}
								className={cn(
									'flex flex-col gap-1 rounded-md border p-3 text-left transition-colors',
									selected ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-gray-200 hover:bg-gray-50',
								)}
							>
								<span className="flex items-center gap-2">
									<LayoutTemplate className="h-4 w-4 shrink-0 text-primary" />
									<span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">{template.name}</span>
									<Badge variant={template.isStarter ? 'secondary' : 'outline'} className="shrink-0 text-[10px]">
										{template.isStarter ? 'Starter' : 'Mine'}
									</Badge>
								</span>
								{template.description && <span className="line-clamp-2 text-xs text-gray-600">{template.description}</span>}
								<span className="text-[11px] text-muted-foreground">
									For a {scopeLabel(template.scope).toLowerCase()}
									{extra ? ` · ${extra}` : ''}
								</span>
							</button>
						)
					})}
				</div>
			)}
		</div>
	)
}

export default DeckTemplateGallery
